'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchMunicipalities } from '@/utils/api';
import styles from './addressSearchInput.module.css';

function getMunicipalityDisplay(m) {
  return m?.name || m?.name_normalized || '';
}

function getMunicipalityShortName(municipalityName) {
  return municipalityName
    .split(/\s+/)
    .map((w, i) => (i === 0 ? w : `${w.slice(0, 3) || w}.`))
    .join(' ');
}

export default function MunicipalityDropdown({
  value = '',
  onChange,
  onMunicipalitySelect = null,
  placeholder = 'Pasirinkite savivaldybę',
  className = '',
  error = false,
  displayValue = null,
}) {
  const [municipalities, setMunicipalities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(displayValue || '');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const wrapperRef = useRef(null);
  const resultsRef = useRef([]);
  const onMunicipalitySelectRef = useRef(onMunicipalitySelect);

  useEffect(() => {
    onMunicipalitySelectRef.current = onMunicipalitySelect;
  }, [onMunicipalitySelect]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchMunicipalities();
        if (!cancelled) {
          setMunicipalities(data || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[MunicipalityDropdown] Load error:', err);
          setLoadError(err.message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const hasDisplayValue =
      displayValue !== null && displayValue !== undefined && displayValue !== '';

    if (hasDisplayValue) {
      setSearchTerm(displayValue);
      if (value) {
        setSelectedMunicipality({ code: value, name: displayValue });
      }
      return;
    }

    if (value) {
      const municipality = municipalities.find(
        m => String(m.code) === String(value)
      );

      if (municipality) {
        const municipalityName = getMunicipalityDisplay(municipality);
        setSearchTerm(municipalityName);
        setSelectedMunicipality(municipality);

        if (onMunicipalitySelectRef.current) {
          onMunicipalitySelectRef.current({
            ...municipality,
            short_name: getMunicipalityShortName(municipalityName),
          });
        }
      } else {
        setSearchTerm(value);
      }
    } else {
      setSelectedMunicipality(null);
    }
  }, [value, displayValue, municipalities]);

  const filtered = searchTerm.trim()
    ? municipalities.filter(
        m =>
          getMunicipalityDisplay(m)
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase())
      )
    : municipalities;
  resultsRef.current = filtered;

  const handleInputChange = e => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setSelectedMunicipality(null);

    if (!newValue || newValue.trim() === '') {
      onChange('');
      setHighlightedIndex(-1);
    } else {
      setHighlightedIndex(0);
    }
    setIsOpen(true);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setHighlightedIndex(filtered.length > 0 ? 0 : -1);
  };

  const handleSelectMunicipality = municipality => {
    setSelectedMunicipality(municipality);
    setSearchTerm(getMunicipalityDisplay(municipality));
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(municipality.code);
    if (onMunicipalitySelect) {
      onMunicipalitySelect(municipality);
    }
  };

  const confirmBestMatchOnBlur = useCallback(() => {
    const trimmed = searchTerm?.trim();
    if (!trimmed || trimmed.length < 2 || selectedMunicipality) return;

    const currentResults = resultsRef.current;
    if (!currentResults.length) return;

    // Only accept an exact typed match — never default to the first filtered row.
    const match = currentResults.find(
      m =>
        getMunicipalityDisplay(m).trim().toLowerCase() ===
        trimmed.toLowerCase()
    );
    if (!match) return;

    handleSelectMunicipality(match);
  }, [searchTerm, selectedMunicipality]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }
      confirmBestMatchOnBlur();
      setIsOpen(false);
      if (selectedMunicipality) {
        setSearchTerm(getMunicipalityDisplay(selectedMunicipality));
      } else if (!value && !searchTerm) {
        setSearchTerm('');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [selectedMunicipality, value, searchTerm, confirmBestMatchOnBlur]);

  if (isLoading) {
    return (
      <div className={`${styles.inputWrapper} ${styles.loading} ${className}`}>
        <div className={styles.iconWrapper}>
          <div className={styles.spinner}></div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`${styles.inputWrapper} ${error ? styles.inputError : ''} ${className}`}>
        <span style={{ color: 'var(--color-error)' }}>Nepavyko įkelti savivaldybių</span>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        className={`${styles.inputWrapper} ${error ? styles.inputError : ''} ${className}`}
      >
        <input
          type="text"
          className={styles.input}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={() => setTimeout(confirmBestMatchOnBlur, 0)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (isOpen && filtered.length > 0) {
                const index = highlightedIndex >= 0 ? highlightedIndex : 0;
                const m = filtered[index];
                if (m) handleSelectMunicipality(m);
              }
            } else if (e.key === 'ArrowDown' && isOpen && filtered.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev =>
                prev < filtered.length - 1 ? prev + 1 : prev
              );
            } else if (e.key === 'ArrowUp' && isOpen && filtered.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>

      {isOpen && (
        <ul className={styles.resultsList}>
          {filtered.length > 0 ? (
            filtered.map((municipality, index) => (
              <li
                key={municipality.code || index}
                className={`${styles.resultItem} ${
                  selectedMunicipality?.code === municipality.code
                    ? styles.active
                    : ''
                } ${
                  (highlightedIndex >= 0 ? highlightedIndex : 0) === index
                    ? styles.highlighted
                    : ''
                }`}
                onMouseDown={e => {
                  e.preventDefault();
                  handleSelectMunicipality(municipality);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className={styles.resultName}>
                  {getMunicipalityDisplay(municipality)}
                </div>
              </li>
            ))
          ) : (
            <li className={styles.noResults}>
              Nerasta rezultatų &ldquo;{searchTerm}&rdquo;
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
